import { useState } from "react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { PageHero } from "@/components/sections/PageHero";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Thank you for your message! We will get back to you soon.");
    setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <Header />
      <main>
        <PageHero title="Contact Us" breadcrumb="Contact" />
        
        <section className="py-[80px] bg-white">
          <div className="container mx-auto px-4 max-w-[1200px]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <h2 className="font-display font-bold text-[28px] text-[#001d3d] mb-6">
                  Get in Touch
                </h2>
                <p className="text-[#4a5568] text-[16px] leading-[1.7] mb-10">
                  Have questions about our products or services? We would love to hear from you. Send us a message and we will respond as soon as possible.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#f8f9fa] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#17A2B8]" />
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-[16px] text-[#001d3d] mb-1">Address</h4>
                      <p className="text-[#4a5568] text-[15px] leading-[1.6]">
                        M/s Basupra Exim International<br />
                        Plot No 162 Industrial Estate,<br />
                        Palus. Dist- Sangli, Maharashtra.<br />
                        India, Pin 416310
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#f8f9fa] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-[#17A2B8]" />
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-[16px] text-[#001d3d] mb-1">Phone</h4>
                      <p className="text-[#4a5568] text-[15px]">
                        <a href="tel:+919890527244" className="hover:text-[#17A2B8] transition-colors">
                          +91-9890527244
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#f8f9fa] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#17A2B8]" />
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-[16px] text-[#001d3d] mb-1">Email</h4>
                      <p className="text-[#4a5568] text-[15px]">
                        <a href="mailto:info@basupraexim.com" className="hover:text-[#17A2B8] transition-colors block">
                          info@basupraexim.com
                        </a>
                        <a href="mailto:exportsbei@yahoo.com" className="hover:text-[#17A2B8] transition-colors block">
                          exportsbei@yahoo.com
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#f8f9fa] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-[#17A2B8]" />
                    </div>
                    <div>
                      <h4 className="font-display font-bold text-[16px] text-[#001d3d] mb-1">Business Hours</h4>
                      <p className="text-[#4a5568] text-[15px]">
                        Monday - Saturday: 9:00 AM - 6:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#f8f9fa] p-8 md:p-10 rounded-lg">
                <h3 className="font-display font-bold text-[22px] text-[#001d3d] mb-6">
                  Send us a Message
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-[#001d3d] text-[14px] font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#e2e8f0] rounded-[4px] text-[15px] focus:outline-none focus:border-[#17A2B8] transition-colors"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[#001d3d] text-[14px] font-medium mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-[#e2e8f0] rounded-[4px] text-[15px] focus:outline-none focus:border-[#17A2B8] transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-[#001d3d] text-[14px] font-medium mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#e2e8f0] rounded-[4px] text-[15px] focus:outline-none focus:border-[#17A2B8] transition-colors"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#001d3d] text-[14px] font-medium mb-2">
                      Subject *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#e2e8f0] rounded-[4px] text-[15px] focus:outline-none focus:border-[#17A2B8] transition-colors bg-white"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="project">Project Inquiry</option>
                      <option value="quote">Request a Quote</option>
                      <option value="support">Technical Support</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#001d3d] text-[14px] font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-[#e2e8f0] rounded-[4px] text-[15px] focus:outline-none focus:border-[#17A2B8] transition-colors resize-none"
                      placeholder="Tell us about your project or inquiry..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 bg-[#17A2B8] text-white font-display font-semibold uppercase tracking-wider rounded-[2px] hover:bg-[#138496] transition-colors"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#f8f9fa]">
          <div className="w-full h-[400px]">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3821.5!2d74.45!3d16.98!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTbCsDU4JzQ4LjAiTiA3NMKwMjcnMDAuMCJF!5e0!3m2!1sen!2sin!4v1600000000000!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Basupra Exim Location"
            />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
